import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Icon,
  Label,
  Message,
  Confirm,
  Loader,
  Pagination,
  Input,
  Card,
  Modal
} from 'semantic-ui-react';
import { QRCodeCanvas } from 'qrcode.react';
import {
  listFiles,
  deleteFile,
  formatFileSize,
  getFileIcon,
  copyToClipboard,
  formatDate
} from '../utils/sharedFilesApi';

const FileList = ({ getApiHeaders, refreshTrigger }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [copiedCodeId, setCopiedCodeId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [qrModalFile, setQrModalFile] = useState(null);
  const [qrCopyStatus, setQrCopyStatus] = useState(null);
  const [canCopyQrImage, setCanCopyQrImage] = useState(false);
  const qrCanvasRef = useRef(null);

  const pageSize = 10;

  useEffect(() => {
    loadFiles();
  }, [getApiHeaders, page, refreshTrigger]);

  useEffect(() => {
    setCanCopyQrImage(!!(navigator.clipboard && window.ClipboardItem));
  }, []);

  const loadFiles = async () => {
    setLoading(true);
    setError(null);

    const result = await listFiles(getApiHeaders, page, pageSize);

    setLoading(false);

    if (result.success) {
      setFiles(result.data.results || []);
      setTotalCount(result.data.count || 0);
      setTotalPages(Math.ceil((result.data.count || 0) / pageSize));
    } else {
      setError(result.error);
    }
  };

  const handleDelete = async (fileId, filename) => {
    setDeleting(true);
    setError(null);

    const result = await deleteFile(getApiHeaders, fileId);

    setDeleting(false);
    setDeleteConfirm(null);

    if (result.success) {
      // Reload files
      loadFiles();
    } else {
      setError(result.error);
    }
  };

  const handleCopyLink = async (publicUrl, fileId) => {
    const success = await copyToClipboard(publicUrl);
    if (success) {
      setCopiedId(fileId);
      setTimeout(() => setCopiedId(null), 2000);
    } else {
      setError('Failed to copy link to clipboard');
    }
  };

  const handleCopyCode = async (code, fileId) => {
    const success = await copyToClipboard(code);
    if (success) {
      setCopiedCodeId(fileId);
      setTimeout(() => setCopiedCodeId(null), 2000);
    } else {
      setError('Failed to copy code to clipboard');
    }
  };

  const handleCopyQrImage = async ({ downloadFallback = false } = {}) => {
    if (!qrCanvasRef.current) {
      setError('QR code is not ready yet');
      return;
    }

    try {
      const canWriteImage = navigator.clipboard && window.ClipboardItem;
      if (canWriteImage) {
        const blob = await new Promise((resolve) =>
          qrCanvasRef.current.toBlob(resolve, 'image/png')
        );

        if (!blob) {
          setError('Failed to generate QR image');
          return;
        }

        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        setQrCopyStatus('Copied QR image to clipboard');
        setTimeout(() => setQrCopyStatus(null), 2000);
        return;
      }

      if (downloadFallback) {
        const dataUrl = qrCanvasRef.current.toDataURL('image/png');
        const safeName = (qrModalFile?.original_filename || 'qr-code')
          .replace(/[^a-z0-9-_]+/gi, '_')
          .replace(/^_+|_+$/g, '');
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = `${safeName || 'qr-code'}.png`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setQrCopyStatus('Downloaded QR code PNG');
        setTimeout(() => setQrCopyStatus(null), 2000);
        return;
      }

      setError('Clipboard image copy is not supported in this browser');
    } catch (err) {
      setError('Failed to copy QR image to clipboard');
    }
  };

  const filteredFiles = files.filter((file) =>
    file.original_filename.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderFileCard = (file) => (
    <Card fluid key={file.id} style={{ marginBottom: '8px' }}>
      <Card.Content style={{ padding: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
              <span style={{ marginRight: '8px', fontSize: '1.2em' }}>{getFileIcon(file.content_type)}</span>
              <div style={{ fontWeight: '600', fontSize: '0.95em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {file.original_filename}
              </div>
            </div>
            {file.description && (
              <div style={{ fontSize: '0.85em', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingLeft: '28px' }}>
                {file.description}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
          <Label
            size="mini"
            style={{ fontSize: '0.75em', cursor: 'pointer' }}
            onClick={() => handleCopyCode(file.code, file.id)}
            color={copiedCodeId === file.id ? 'green' : undefined}
            title="Click to copy code"
          >
            <Icon name={copiedCodeId === file.id ? 'check' : 'code'} />
            {file.code}
          </Label>
          <Label size="mini" style={{ fontSize: '0.75em' }}>
            <Icon name="database" />
            {formatFileSize(file.file_size)}
          </Label>
          <Label size="mini" style={{ fontSize: '0.75em' }}>
            <Icon name="download" />
            {file.download_count}
          </Label>
          {file.is_expired ? (
            <Label color="red" size="mini" style={{ fontSize: '0.75em' }}>
              <Icon name="warning" />
              Expired
            </Label>
          ) : file.expires_at ? (
            <Label size="mini" style={{ fontSize: '0.75em' }}>
              <Icon name="clock" />
              {formatDate(file.expires_at)}
            </Label>
          ) : (
            <Label color="green" size="mini" style={{ fontSize: '0.75em' }}>
              <Icon name="infinity" />
              Never
            </Label>
          )}
        </div>

        <Button.Group fluid size="tiny">
          <Button
            primary
            onClick={() => handleCopyLink(file.public_url, file.id)}
            disabled={file.is_expired}
          >
            <Icon name={copiedId === file.id ? 'check' : 'copy'} />
            {copiedId === file.id ? 'Copied' : 'Copy'}
          </Button>
          <Button
            color="teal"
            onClick={() => {
              setQrCopyStatus(null);
              setQrModalFile(file);
            }}
            disabled={file.is_expired}
          >
            <Icon name="qrcode" />
            QR
          </Button>
          <Button
            negative
            onClick={() => setDeleteConfirm(file)}
          >
            <Icon name="trash" />
            Delete
          </Button>
        </Button.Group>
      </Card.Content>
    </Card>
  );


  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Loader active inline="centered">
          Loading files...
        </Loader>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.1em' }}>
            <Icon name="file alternate" />
            Files
            {totalCount > 0 && (
              <Label circular color="blue" size="mini" style={{ marginLeft: '8px' }}>
                {totalCount}
              </Label>
            )}
          </h3>
        </div>
      </div>

      <div style={{ marginBottom: '15px' }}>
        <Input
          icon="search"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fluid
          size="small"
        />
      </div>

      {error && (
        <Message negative onDismiss={() => setError(null)} size="tiny">
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {filteredFiles.length === 0 ? (
        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
          <Icon name="inbox" size="big" color="grey" />
          <p style={{ marginTop: '10px', color: '#666', fontSize: '0.9em' }}>
            {searchTerm ? 'No files match your search' : 'No files uploaded yet'}
          </p>
        </div>
      ) : (
        <div>
          {filteredFiles.map((file) => renderFileCard(file))}
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ marginTop: '15px', textAlign: 'center' }}>
          <Pagination
            activePage={page}
            totalPages={totalPages}
            onPageChange={(e, { activePage }) => setPage(activePage)}
            size="mini"
            siblingRange={0}
            boundaryRange={0}
            ellipsisItem={{ content: <Icon name="ellipsis horizontal" />, icon: true }}
            firstItem={null}
            lastItem={null}
            prevItem={{ content: <Icon name="angle left" />, icon: true }}
            nextItem={{ content: <Icon name="angle right" />, icon: true }}
          />
        </div>
      )}

      <Confirm
        open={!!deleteConfirm}
        header="Delete File"
        content={
          deleteConfirm
            ? `Are you sure you want to delete "${deleteConfirm.original_filename}"? This action cannot be undone.`
            : ''
        }
        confirmButton="Delete"
        cancelButton="Cancel"
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={() => deleteConfirm && handleDelete(deleteConfirm.id, deleteConfirm.original_filename)}
        size="mini"
      />

      <Modal
        open={!!qrModalFile}
        onClose={() => {
          setQrCopyStatus(null);
          setQrModalFile(null);
        }}
        size="tiny"
      >
        <Modal.Header>
          <Icon name="qrcode" />
          QR Code for {qrModalFile?.original_filename}
        </Modal.Header>
        <Modal.Content style={{ textAlign: 'center', padding: '30px' }}>
          {qrModalFile && (
            <div>
              <QRCodeCanvas
                ref={qrCanvasRef}
                value={qrModalFile.public_url}
                size={256}
                level="H"
                style={{ padding: '16px', background: 'white', cursor: 'pointer' }}
                onClick={() => handleCopyQrImage({ downloadFallback: !canCopyQrImage })}
                title="Click to copy QR code image"
              />
              <div style={{ marginTop: '8px', fontSize: '0.8em', color: qrCopyStatus ? '#2185d0' : '#666' }}>
                {qrCopyStatus || (canCopyQrImage ? 'Click the QR code to copy it as a PNG' : 'Click the QR code or use "Download PNG"')}
              </div>
              <div style={{ marginTop: '20px', fontSize: '0.9em', color: '#666', wordBreak: 'break-all' }}>
                {qrModalFile.public_url}
              </div>
            </div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button
            primary
            onClick={() => handleCopyQrImage({ downloadFallback: !canCopyQrImage })}
          >
            {canCopyQrImage ? 'Copy PNG' : 'Download PNG'}
          </Button>
          <Button onClick={() => setQrModalFile(null)}>
            Close
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default FileList;
